/**
 * auditApi.ts — Full audit data service API port
 *
 * AngularJS source: services/auditDataService.js (694 LOC, 50 methods)
 *
 * This file ports the API call methods from auditDataService.
 * Permission helper functions are in useAuditPermissions.ts (separate file).
 * Pure logic functions (addValidation, QuestionAuditFailureCalculation) are
 * inlined in the components that use them.
 *
 * Methods already ported in auditDashboardApi.ts:
 * - listTasksByWorkflow, addTaskUI, listBillingReasons, updateTaskNoBilling
 * - listCategories, getImageViewerURL, getDisputeCategories, getDisputeCategoriesByParentId
 * - getValidReasons, setFinancialWorkFlow, setWorkOrderState, etc.
 */

import { axiosClient } from '@/lib/axiosClient';
import { buildApiBase } from '@/lib/buildApiBase';
import { getPermission } from '@/lib/permissionsApi';
import { obfuscateText } from '@/lib/obfuscate';

const API_BASE = `${buildApiBase()}/API`;

// ============ OBFUSCATION POST-PROCESSING ============

/**
 * Apply viewPermission-based obfuscation to one answer string.
 * Port of auditDataService.obfuscateAnswer() (lines 404-415).
 */
function obfuscateAnswer(
  text: string | null | undefined,
  pattern: string,
  obfuscateFromDate: boolean
): string | null {
  if (!text) return null;
  let value = text;
  if (obfuscateFromDate) {
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      value = `${mm}/${dd}/${yyyy}`;
    }
  }
  return obfuscateText(value, pattern);
}

/**
 * Post-process a GetPropertyQuestionAnswers response to apply per-question
 * obfuscation rules from `viewPermission`. Mirrors AngularJS
 * auditDataService.handleResponse() (lines 171-216).
 *
 * Rules per question:
 *  - If `viewPermission` is set AND user has it WITH a role (e.g. R4) →
 *    apply that mask role to each answer.
 *  - If `viewPermission` is set AND user has it WITHOUT a role → leave answers as-is.
 *  - If `viewPermission` is set AND user does NOT have it → mask everything (R0).
 *  - If `viewPermission` is unset → mask everything (R0).
 *  - Date questions that get obfuscated have `questionType` flipped to
 *    `'ObfuscatedDate'` so downstream code doesn't try to date-format the asterisks.
 */
function handlePropertyQuestionAnswersResponse(
  data: PropertyQuestionAnswer[]
): PropertyQuestionAnswer[] {
  if (!Array.isArray(data)) return data;

  for (const parentGroup of data) {
    const questions = parentGroup.propertyQuestionAnswers || [];
    for (const question of questions) {
      const answers = question.answerValues || [];
      const questionIsDate = question.questionType === 'Date';
      let textObfuscated = false;
      let obfuscatedQuestion = false;

      if (question.viewPermission) {
        const permissionResult = getPermission(question.viewPermission);
        if (permissionResult.validPermission) {
          if (permissionResult.role) {
            for (let a = 0; a < answers.length; a++) {
              textObfuscated = true;
              const obf = obfuscateAnswer(answers[a], permissionResult.role, questionIsDate);
              if (obf !== null) answers[a] = obf;
            }
          }
          // valid permission, no role → show full text (no-op)
        } else {
          obfuscatedQuestion = true;
        }
      } else {
        obfuscatedQuestion = true;
      }

      if (obfuscatedQuestion) {
        for (let a = 0; a < answers.length; a++) {
          textObfuscated = true;
          const obf = obfuscateAnswer(answers[a], 'R0', questionIsDate);
          if (obf !== null) answers[a] = obf;
        }
      }

      if (questionIsDate && textObfuscated) {
        question.questionType = 'ObfuscatedDate';
      }
    }
  }

  return data;
}

// ============ TYPES ============

export interface AuditResult {
  questions: AuditQuestion[];
  [key: string]: unknown;
}

export interface AuditQuestion {
  questionKey?: string;
  questionType?: string;
  questionText?: string;
  viewPermission?: string;
  answerValues?: string[];
  availableAnswers?: { outputText: string; key: string; value: string }[];
  priorAnswers?: string[];
  auditFailures?: AuditFailure[];
  finalRuleType?: number;
  finalShowDisplayText?: boolean;
  disabledKeepExisitng?: boolean;
  Validation?: unknown;
  [key: string]: unknown;
}

export interface AuditFailure {
  ruleFailureType: number;
  showDisplayText: boolean;
  displayText?: string;
}

/**
 * QuestionAuditFailureCalculation — pure port of legacy
 * auditDataService.js:597-619.
 *
 * For each question, picks the first auditFailure where
 * `ruleFailureType in (1, 2)` AND `showDisplayText` is true, falling back to
 * the first matching failure with `showDisplayText` false. Writes the chosen
 * `ruleFailureType` to `question.finalRuleType` and the chosen
 * `showDisplayText` to `question.finalShowDisplayText` — these power the
 * results-row tooltip icon (categories.template.html:107-122,
 * categories.style.css `.autoWithText` / `.autoWithOutText` classes).
 *
 * Mutates the questions in place to match legacy semantics, then returns the
 * same array so callers can chain (e.g. `setResults(QuestionAuditFailureCalculation(data))`).
 */
export function questionAuditFailureCalculation<T extends { questions?: AuditQuestion[] }>(
  auditResults: T[]
): T[] {
  for (const category of auditResults) {
    const questions = (category.questions as AuditQuestion[] | undefined) || [];
    for (const question of questions) {
      const failures = (question.auditFailures as AuditFailure[] | undefined) || [];
      const withText = failures.find(
        (af) => (af.ruleFailureType === 1 || af.ruleFailureType === 2) && af.showDisplayText
      );
      const withoutText = failures.find(
        (af) => (af.ruleFailureType === 1 || af.ruleFailureType === 2) && !af.showDisplayText
      );
      if (withText) {
        question.finalRuleType = withText.ruleFailureType;
        question.finalShowDisplayText = withText.showDisplayText;
      } else if (withoutText) {
        question.finalRuleType = withoutText.ruleFailureType;
        question.finalShowDisplayText = withoutText.showDisplayText;
      }
    }
  }
  return auditResults;
}

export interface AuditDecision {
  id: number;
  name: string;
  [key: string]: unknown;
}

export interface AuditDecisionReason {
  id: number;
  // Backend returns `text` (matches legacy `ng-options="reason.text for reason in audit.reasons"`).
  // `reason` is kept optional for backward compatibility with any older callers.
  text?: string;
  reason?: string;
  [key: string]: unknown;
}

export interface SubmissionReason {
  id: number;
  reason: string;
  [key: string]: unknown;
}

export interface PropertyQuestionAnswer {
  questionText?: string;
  propertyQuestionAnswers?: {
    questionText: string;
    answerValues: string[];
    questionType: string;
    viewPermission?: string;
    tier?: number;
    [key: string]: unknown;
  }[];
  [key: string]: unknown;
}

export interface QuestionTransactionHistory {
  questionType: string;
  viewPermission?: string;
  history: {
    answers: string[];
    [key: string]: unknown;
  }[];
  [key: string]: unknown;
}

export interface WorkOrderQuestionAnswer {
  [key: string]: unknown;
}

export interface WDSOrderResult {
  workOrderResultId: number;
}

export interface FITResult {
  success: boolean;
  message?: string;
}

export interface AssetDamage {
  [key: string]: unknown;
}

// ============ API FUNCTIONS ============

/**
 * GET /API/Submission/GetResultsForAudit or GetResultsForSubmission
 * AngularJS: auditDataService.GetResultsList(id, refresh, itemType)
 */
export async function getResultsList(
  id: number | string,
  refresh: boolean,
  itemType: string | null
) {
  if (itemType === 'SUBMISSION' || itemType === 'ORDERREQUEST') {
    const response = await axiosClient.get<AuditResult[]>(
      `${API_BASE}/Submission/GetResultsForSubmission`,
      { params: { parentSubmissionId: id, refresh } }
    );
    return response.data;
  } else {
    const response = await axiosClient.get<AuditResult[]>(
      `${API_BASE}/Submission/GetResultsForAudit`,
      { params: { orderNumber: id, refresh } }
    );
    return response.data;
  }
}

/**
 * GET /API/Decisions/GetAuditDecisions
 * AngularJS: auditDataService.GetAuditDecisions()
 */
export async function getAuditDecisions() {
  const response = await axiosClient.get<AuditDecision[]>(
    `${API_BASE}/Decisions/GetAuditDecisions`
  );
  return response.data;
}

/**
 * GET /API/Decisions/GetAuditDecisionReasons
 * AngularJS: auditDataService.GetAuditDecisionReasons(auditDecisionId, source)
 */
export async function getAuditDecisionReasons(auditDecisionId: number, source: string) {
  const response = await axiosClient.get<AuditDecisionReason[]>(
    `${API_BASE}/Decisions/GetAuditDecisionReasons`,
    { params: { auditDecisionId, source } }
  );
  return response.data;
}

/**
 * PUT /API/Submission/PutSubmissionQuestion
 * AngularJS: auditDataService.UpdateSubmissionQuestion(...)
 */
export async function updateSubmissionQuestion(params: {
  submissionQuestionId: number;
  auditDecisionId: number;
  auditDecisionReasonId: number | null;
  comments: string | null;
  value?: string | null;
  answerIds?: number[];
}) {
  const response = await axiosClient.put(`${API_BASE}/Submission/PutSubmissionQuestion`, params);
  return response.data;
}

/**
 * GET /API/AuditTask/TaskSetTransitionState
 * AngularJS: auditDataService.TaskSetTransitionState(...)
 */
export async function taskSetTransitionState(
  workflowStateTransitionId: number,
  toStateId: number,
  taskId: number,
  reason: number,
  comments: string
) {
  const response = await axiosClient.get(`${API_BASE}/AuditTask/TaskSetTransitionState`, {
    params: {
      workflowStateTransitionId,
      workflowStateId: toStateId,
      taskId,
      reasonId: reason,
      comments,
    },
  });
  return response.data;
}

/**
 * POST /API/AuditTask/UpdateTaskUI
 * AngularJS: auditDataService.UpdateTaskUI(objBaseUI)
 */
export async function updateTaskUI(requestBody: Record<string, unknown>) {
  const response = await axiosClient.post(`${API_BASE}/AuditTask/UpdateTaskUI`, requestBody);
  return response.data;
}

/**
 * PUT /API/Submission/AcceptSubmissionQuestion
 * AngularJS: auditDataService.AcceptSubmissionQuestions(submissionQuestionIds)
 */
export async function acceptSubmissionQuestions(submissionQuestionIds: number[]) {
  const response = await axiosClient.put(
    `${API_BASE}/Submission/AcceptSubmissionQuestion`,
    submissionQuestionIds
  );
  return response.data;
}

/**
 * POST /API/Property/ProcessSubmission
 * AngularJS: auditDataService.ProcessSubmission(updatedPropertyQuestions)
 */
export async function processSubmission(updatedPropertyQuestions: Record<string, unknown>) {
  const response = await axiosClient.post(
    `${API_BASE}/Property/ProcessSubmission/`,
    updatedPropertyQuestions
  );
  return response.data;
}

/**
 * GET /API/Property/ListSubmissionReasons
 * AngularJS: auditDataService.ListSubmissionReasons()
 */
export async function listSubmissionReasons() {
  const response = await axiosClient.get<SubmissionReason[]>(
    `${API_BASE}/Property/ListSubmissionReasons/`
  );
  return response.data;
}

/**
 * GET /API/Submission/ListParentSubmissions
 * AngularJS: auditDataService.ListParentSubmissions(id, type)
 */
export async function listParentSubmissions(id: number, type: string) {
  const response = await axiosClient.get(`${API_BASE}/Submission/ListParentSubmissions`, {
    params: { id, type },
  });
  return response.data;
}

/**
 * GET /API/Property/GetPropertyQuestionAnswers
 * AngularJS: auditDataService.GetPropertyQuestionAnswers(propertyLoanId, startDate, assetId, questionId, module)
 * Applies viewPermission-based obfuscation via handlePropertyQuestionAnswersResponse,
 * matching auditDataService.handleResponse() (lines 171-216).
 */
export async function getPropertyQuestionAnswers(
  propertyLoanId: number,
  startDate: string | null,
  assetId: number | null,
  questionId: number | null,
  module: string | null | undefined
) {
  const response = await axiosClient.get<PropertyQuestionAnswer[]>(
    `${API_BASE}/Property/GetPropertyQuestionAnswers`,
    { params: { propertyLoanId, questionId, assetId, startDate, module: module || undefined } }
  );
  return handlePropertyQuestionAnswersResponse(response.data);
}

/**
 * GET /API/Property/GetQuestionTransactionHistory
 * AngularJS: auditDataService.GetQuestionTransactionHistory(propertyLoanId, questionId, assetId)
 */
export async function getQuestionTransactionHistory(
  propertyLoanId: number,
  questionId: number,
  assetId: number | null
) {
  const response = await axiosClient.get<QuestionTransactionHistory>(
    `${API_BASE}/Property/GetQuestionTransactionHistory`,
    { params: { propertyLoanId, questionId, assetId } }
  );
  return response.data;
}

/**
 * GET /API/Submission/GetWorkOrderQuestionAnswers
 * AngularJS: auditDataService.GetWorkOrderQuestionAnswers(orderNumber, sourceSystem, assetId)
 */
export async function getWorkOrderQuestionAnswers(
  orderNumber: number,
  sourceSystem: string,
  assetId: number | null
) {
  const response = await axiosClient.get<WorkOrderQuestionAnswer[]>(
    `${API_BASE}/Submission/GetWorkOrderQuestionAnswers`,
    { params: { orderNumber, sourceSystem, assetId } }
  );
  return response.data;
}

/**
 * GET /API/WorkOrderInfo/GetWorkOrderQuestionAnswersByModule
 * AngularJS: auditDataService.GetWorkOrderQuestionAnswersByModule(orderNumber, module)
 */
export async function getWorkOrderQuestionAnswersByModule(orderNumber: number, module: string) {
  const response = await axiosClient.get(
    `${API_BASE}/WorkOrderInfo/GetWorkOrderQuestionAnswersByModule`,
    { params: { orderNumber, module } }
  );
  return response.data;
}

/**
 * POST /API/Submission/WorkOrderProcessSubmission
 * AngularJS: auditDataService.WorkOrderProcessSubmission(updatedWorkOrderQuestions)
 */
export async function workOrderProcessSubmission(
  updatedWorkOrderQuestions: Record<string, unknown>
) {
  const response = await axiosClient.post(
    `${API_BASE}/Submission/WorkOrderProcessSubmission/`,
    updatedWorkOrderQuestions
  );
  return response.data;
}

/**
 * GET /API/Property/GetQuestionAndAnswerAuditTrail
 * AngularJS: auditDataService.GetQuestionAndAnswerAuditTrail(parentSubmissionId, questionId, assetId)
 */
export async function getQuestionAndAnswerAuditTrail(
  parentSubmissionId: number,
  questionId: number,
  assetId: number | null
) {
  const response = await axiosClient.get(`${API_BASE}/Property/GetQuestionAndAnswerAuditTrail`, {
    params: { parentSubmissionId, questionId, assetId },
  });
  return response.data;
}

/**
 * GET vmapi /API/VendorManagement/ServiceAreaSearch
 * AngularJS: vendorManagementService.ServiceAreaSearch(serviceArea)
 *
 * Returns the vendor + company + serviceArea + contacts payload used to render
 * the inline vendor info popout (owner name, phone, email).
 */
export interface ServiceAreaSearchResponse {
  companyViewModel?: { company?: { ownerName?: string; email?: string } };
  companies?: {
    company?: {
      serviceAreas?: {
        contacts?: { phoneBusiness?: string }[];
      }[];
    };
  }[];
}

export async function serviceAreaSearch(serviceArea: string): Promise<ServiceAreaSearchResponse> {
  const VM_API_BASE = `${buildApiBase('vmapi')}/API/VendorManagement`;
  const response = await axiosClient.get<ServiceAreaSearchResponse>(
    `${VM_API_BASE}/ServiceAreaSearch`,
    { params: { ServiceArea: serviceArea } }
  );
  return response.data;
}

/**
 * GET /API/AssetDamage/GetAssetDamagesByTaskId
 * AngularJS: auditDataService.GetAssetDamagesByTaskId(taskId)
 */
export async function getAssetDamagesByTaskId(taskId: number) {
  const response = await axiosClient.get<AssetDamage[]>(
    `${API_BASE}/AssetDamage/GetAssetDamagesByTaskId`,
    { params: { taskId, addCollapseInfo: true } }
  );
  return response.data;
}

/**
 * GET /API/Submission/GetFITPreview
 * AngularJS: auditDataService.fitPreview(orderNumber)
 */
export async function fitPreview(orderNumber: number) {
  const response = await axiosClient.get<FITResult>(`${API_BASE}/Submission/GetFITPreview/`, {
    params: { orderNumber },
  });
  return response.data;
}

/**
 * GET /API/Submission/GetFITCommit
 * AngularJS: auditDataService.fitCommit(orderNumber)
 */
export async function fitCommit(orderNumber: number) {
  const response = await axiosClient.get<FITResult>(`${API_BASE}/Submission/GetFITCommit/`, {
    params: { orderNumber },
  });
  return response.data;
}

/**
 * GET /API/Submission/GetFarmersRCT
 * AngularJS: auditDataService.farmersRCT(orderNumber)
 */
export async function farmersRCT(orderNumber: number) {
  const response = await axiosClient.get<FITResult>(`${API_BASE}/Submission/GetFarmersRCT/`, {
    params: { orderNumber },
  });
  return response.data;
}

/**
 * GET /API/AuditTask/GetWDSOrder
 * AngularJS: auditDataService.GetWDSOrder(...)
 */
export async function getWDSOrder(
  itemTypeRecordId: string,
  currentOrderStatus: string,
  currentShiftStatus: string,
  currentWDSQueue: string
) {
  const response = await axiosClient.get<WDSOrderResult>(`${API_BASE}/AuditTask/GetWDSOrder`, {
    params: { itemTypeRecordId, currentOrderStatus, currentShiftStatus, currentWDSQueue },
  });
  return response.data;
}

/**
 * GET /API/AuditTask/GetShiftOptions
 * AngularJS: auditDataService.getShiftOptions()
 */
export async function getShiftOptions() {
  const response = await axiosClient.get<string[]>(`${API_BASE}/AuditTask/GetShiftOptions`);
  return response.data;
}

/**
 * GET /API/Clients/GetClientAttributeValues
 * AngularJS: auditDataService.getClientAttributeValues(attributeName, propertyLoanId)
 */
export async function getClientAttributeValues(attributeName: string, propertyLoanId: number) {
  const response = await axiosClient.get(`${API_BASE}/Clients/GetClientAttributeValues`, {
    params: { attributeName, propertyLoanId },
  });
  return response.data;
}

/**
 * GET /API/Submission/GetRCTOrderValuation
 * AngularJS: auditDataService.valuateOrder(orderNumber)
 */
export async function valuateOrder(orderNumber: number) {
  const response = await axiosClient.get(`${API_BASE}/Submission/GetRCTOrderValuation/`, {
    params: { orderNumber },
  });
  return response.data;
}

/**
 * GET /API/Submission/GetFITRePreviewAndCommitClientCode
 * AngularJS: auditDataService.getFITRePreviewAndCommitClientCode()
 */
export async function getFITRePreviewAndCommitClientCode() {
  const response = await axiosClient.get(
    `${API_BASE}/Submission/GetFITRePreviewAndCommitClientCode`
  );
  return response.data;
}

/**
 * GET /API/Property/GetPropertyQuestionAnswers2 (loan qualifier)
 * AngularJS: auditDataService.getLoanQualifierInfo(propertyLoanId, clientCode, loanNumber)
 * Applies viewPermission-based obfuscation — matches auditDataService.js line 219
 * which pipes the response through handleResponse.
 */
export async function getLoanQualifierInfo(
  propertyLoanId: number,
  clientCode: string,
  loanNumber: string
) {
  const response = await axiosClient.get<PropertyQuestionAnswer[]>(
    `${API_BASE}/Property/GetPropertyQuestionAnswers2`,
    { params: { propertyLoanId, clientCode, loanNumber, module: 'loanqualifier' } }
  );
  return handlePropertyQuestionAnswersResponse(response.data);
}

/**
 * GET /API/ImageCategory/GetDashboardImages
 * AngularJS: imageService.getWorkOrderImages(orderNumber)
 */
export async function getWorkOrderImages(orderNumber: number) {
  const response = await axiosClient.get<unknown[]>(
    `${API_BASE}/ImageCategory/GetDashboardImages`,
    { params: { orderNumber } }
  );
  return response.data;
}

/**
 * GET /API/AuditTask/GetTasksForAudit
 * AngularJS: auditDataService.GetTasksForAudit(propertyId, assetId, startDate, module)
 */
export async function getTasksForAudit(
  propertyId: number,
  assetId: number | null,
  startDate: string | null,
  module: string
) {
  const response = await axiosClient.get(`${API_BASE}/AuditTask/GetTasksForAudit`, {
    params: { propertyId, assetId, startDate, module },
  });
  return response.data;
}

// ============ GENERAL DATA SERVICE API FUNCTIONS ============
// These are from generalDataService but needed by the audit dashboard

/**
 * POST /API/AssetInfo/ListAssets
 * AngularJS: generalDataService.ListAssets(orderNumber, propertyLoanId, propertyId, multiUnitExcludeDefault)
 */
export async function listAssets(propertyLoanId: number | null, propertyId: number | null) {
  const params: Record<string, unknown> = {};
  if (propertyLoanId) params.propertyLoanId = propertyLoanId;
  if (propertyId) params.propertyId = propertyId;
  const response = await axiosClient.post<AssetUnit[]>(
    `${API_BASE}/AssetInfo/ListAssets/`,
    params,
    { silent401: true }
  );
  return response.data;
}

export interface AssetUnit {
  id: number;
  assetName: string;
  propertyLoanId?: number;
  [key: string]: unknown;
}

/**
 * GET /API/WorkOrderInfo/GetWorkflowStateCodesForScheduling
 * AngularJS: generalDataService.getSchedulingWorkFlowStateCodes()
 */
export async function getSchedulingWorkFlowStateCodes() {
  const WOI_BASE = `${buildApiBase()}/API/WorkOrderInfo`;
  const response = await axiosClient.get<string[]>(
    `${WOI_BASE}/GetWorkflowStateCodesForScheduling/`,
    { silent401: true }
  );
  return response.data;
}

/** Departments whose Order Status Tracker links should open the location map. */
export async function getDepartmentsForOrderStatusTrackerToLocation(): Promise<string[]> {
  const WOI_BASE = `${buildApiBase()}/API/WorkOrderInfo`;
  const response = await axiosClient.get<string[]>(
    `${WOI_BASE}/GetDepartmentsForOrderStatusTrackerToLocation/`,
    { silent401: true }
  );
  return response.data;
}

/**
 * GET /API/WorkOrderInfo/GetWorkcodesForSACCButton
 * AngularJS: generalDataService.getSACCButtonWorkCodes()
 */
export async function getSACCButtonWorkCodes() {
  const WOI_BASE = `${buildApiBase()}/API/WorkOrderInfo`;
  const response = await axiosClient.get<string[]>(`${WOI_BASE}/GetWorkcodesForSACCButton/`, {
    silent401: true,
  });
  return response.data;
}

/**
 * GET /API/Clients/GetClientLuggage
 * AngularJS: generalDataService.getClientAttributeLuggage(attributeName, propertyLoanId, orderNumber)
 */
export async function getClientAttributeLuggage(
  attributeName: string,
  propertyLoanId: number,
  orderNumber: number
) {
  const response = await axiosClient.get<string[]>(`${API_BASE}/Clients/GetClientLuggage`, {
    params: { attributeName, propertyLoanId, orderNumber },
    silent401: true,
  });
  return response.data;
}

/**
 * GET /API/1.1/PropertyLoanLite/SG/{propertyLoanId}/{orderNumber}
 * AngularJS: generalDataService.getWOLuggage(propertyLoanId, orderNumber)
 * NOTE: This endpoint is on spiglassgoapi, not spiglassapi
 */
export async function getWOLuggage(propertyLoanId: number, orderNumber: number) {
  const GO_API_BASE = buildApiBase('spiglassgoapi');
  const response = await axiosClient.get<{
    questions: Record<string, { answers: { value: string }[] }>;
  }>(`${GO_API_BASE}/API/1.1/PropertyLoanLite/SG/${propertyLoanId}/${orderNumber}`, {
    silent401: true,
  });
  return response.data;
}

/**
 * POST /API/PDF/CreatePDF
 * AngularJS: generalDataService.createPDF(orderNumber, images, category, task)
 */
export async function createPDF(orderNumber: number, imageIDs: (number | string)[], task: string) {
  const response = await axiosClient.post(`${API_BASE}/PDF/CreatePDF`, {
    orderNumber,
    imageIDs,
    task,
  });
  return response.data;
}

// ============ TASK IMAGE SERVICE (widget-gallery5 area='task') ============
// AngularJS source: services/imageService.js — deleteTaskImages / highlightTaskImages /
// addTaskImages / listTaskImages. Body shape matches widgetGallery5 objTaskImageDetails.

/** imageService.deleteTaskImages — POST /API/ImageCategory/DeleteTaskImages */
export async function deleteTaskImages(taskId: number, imageIds: number[]) {
  const response = await axiosClient.post(`${API_BASE}/ImageCategory/DeleteTaskImages`, {
    ImageIds: imageIds,
    TaskId: taskId,
  });
  return response.data;
}

/** imageService.highlightTaskImages — POST /API/ImageCategory/HighLightTaskImages */
export async function highlightTaskImages(taskId: number, imageIds: number[]) {
  const response = await axiosClient.post(`${API_BASE}/ImageCategory/HighLightTaskImages`, {
    ImageIds: imageIds,
    TaskId: taskId,
  });
  return response.data;
}

/** imageService.addTaskImages — POST /API/ImageCategory/AddTaskImages */
export async function addTaskImages(taskId: number, imageIds: (number | string)[]) {
  const response = await axiosClient.post(`${API_BASE}/ImageCategory/AddTaskImages`, {
    ImageIds: imageIds,
    TaskId: taskId,
  });
  return response.data;
}

/** imageService.listTaskImages — GET /API/ImageCategory/ListTaskImages */
export async function listTaskImages(taskId: number, orderNumber: number) {
  const response = await axiosClient.get<unknown[]>(`${API_BASE}/ImageCategory/ListTaskImages`, {
    params: { taskId, orderNumber },
  });
  return response.data;
}

// ============ QUESTION IMAGE SERVICE (widget-gallery3 audit-question images) ============
// AngularJS source: services/imageService.js — deleteQuestionImages / addQuestionImages /
// listQuestionImages. Body shape matches widgetGallery3 $scope.objQImageDetails:
// { ImageIds: number[], SubmissionQuestionId: number }. Note: question images have
// NO highlight endpoint in legacy — only Task and Bid images support highlighting.

/** imageService.deleteQuestionImages — POST /API/ImageCategory/DeleteQuestionImages */
export async function deleteQuestionImages(submissionQuestionId: number, imageIds: number[]) {
  const response = await axiosClient.post(`${API_BASE}/ImageCategory/DeleteQuestionImages`, {
    ImageIds: imageIds,
    SubmissionQuestionId: submissionQuestionId,
  });
  return response.data;
}

/** imageService.addQuestionImages — POST /API/ImageCategory/AddQuestionImages */
export async function addQuestionImages(
  submissionQuestionId: number,
  imageIds: (number | string)[]
) {
  const response = await axiosClient.post(`${API_BASE}/ImageCategory/AddQuestionImages`, {
    ImageIds: imageIds,
    SubmissionQuestionId: submissionQuestionId,
  });
  return response.data;
}

/** imageService.listQuestionImages — GET /API/ImageCategory/ListQuestionImages */
export async function listQuestionImages(submissionQuestionId: number, orderNumber: number) {
  const response = await axiosClient.get<unknown[]>(
    `${API_BASE}/ImageCategory/ListQuestionImages`,
    { params: { questionId: submissionQuestionId, orderNumber } }
  );
  return response.data;
}

// ============ BIDS DATA SERVICE API FUNCTIONS ============

export interface LinkedBid {
  bid: {
    id: number;
    workOrder?: { orderNumber?: number };
    vendorCompleteDate?: string;
    bidType?: {
      name?: string;
      parentCategory?: { name?: string };
    };
    clientTotal?: number;
    description?: string;
    workflowStateTransition?: { displayStatus?: string };
    approvalRequests?: {
      workflowStateTransition?: { displayStatus?: string };
      bidTasks?: { workflowStateTransition?: { displayStatus?: string } }[];
    }[];
    workflowStateTransitionId?: number;
  };
  linked: boolean;
}

/**
 * GET /API/BidTask/GetLinkedBidsForTask
 * AngularJS: bidsDataService.GetLinkedBidsForTask(taskid)
 */
export async function getLinkedBidsForTask(taskId: number) {
  const response = await axiosClient.get<LinkedBid[]>(`${API_BASE}/BidTask/GetLinkedBidsForTask/`, {
    params: { taskid: taskId },
  });
  return response.data;
}

/**
 * POST /API/BidTask/UpdateLinkedBidForTask
 * AngularJS: bidsDataService.UpdateLinkedBidsForTask(taskId, bidId, linked)
 */
export async function updateLinkedBidForTask(taskId: number, bidId: number, linked: boolean) {
  const response = await axiosClient.post(`${API_BASE}/BidTask/UpdateLinkedBidForTask`, {
    taskId,
    bidId,
    linked,
  });
  return response.data;
}
